/**
 * Physics constants shared between client and server
 *
 * IMPORTANT: All physics-related values must be defined here to ensure
 * client/server consistency and prevent desync bugs.
 */
// ============== Player Movement ==============
/** Base movement speed in meters per second */
export const MOVE_SPEED = 6.0;
/**
 * How fast the movement direction rotates toward the target (exponential smoothing rate).
 * τ = 1/rate = 25 ms — fast enough that a strafe reads as instant, slow enough to keep the
 * animation blend from snapping. At 24 (τ = 42 ms) playtesters reported turns as "delayed".
 */
export const DIRECTION_SMOOTH_SPEED = 40;
/** Multiplier applied when sprinting */
export const SPRINT_MULTIPLIER = 1.6;
/** Jump impulse velocity in m/s */
export const JUMP_VELOCITY = 15.0;
// ============== Player Physics ==============
/** Gravity acceleration in m/s² (negative = downward) */
export const GRAVITY = -40.0;
// ============== Fixed-Step Voxel Physics ==============
// The voxel physics engine (shared/src/physics) runs on a fixed tick, decoupled from the
// render frame via an accumulator. 120 Hz divides cleanly against SERVER_TICK_HZ=20 and
// CLIENT_INPUT_HZ=30 for future server-side validation/reconciliation.
/**
 * Fixed physics tick rate in Hz.
 *
 * Every latency term in the loop is a fraction of one tick: input sampled per frame is
 * consumed by the next tick, and the render eye interpolates between the last two ticks.
 * At 60 Hz that stack peaked around 33 ms and playtesters read it as "sluggish and delayed";
 * 120 Hz halves it to ~16 ms worst case, ~8 ms typical. Measured cost is 43-77 µs/tick, so
 * the extra 60 ticks/s buy this for under 1% of wall clock (R60 asserts both figures).
 */
export const PHYSICS_TICK_RATE = 120;
/** Fixed physics timestep in seconds */
export const PHYSICS_FIXED_DT = 1 / PHYSICS_TICK_RATE;
/**
 * Max physics ticks executed per render frame. The render loop clamps a frame delta at
 * 100 ms, and 12 × 8.33 ms = 100 ms, so a legal frame is always simulated in full; beyond
 * the clamp, excess accumulated time is DROPPED (brief slow-motion) rather than spiralling.
 */
export const MAX_TICKS_PER_FRAME = 12;
/** Terminal fall speed in m/s (bounds per-tick displacement to ~0.42 m at 120 Hz) */
export const TERMINAL_VELOCITY = 50.0;
/**
 * Max displacement per collision sub-move, in meters (PLAYER_RADIUS / 2). Ticks whose
 * motion exceeds this are internally subdivided; the vertical/horizontal probes are
 * swept-exact anyway, this keeps the whisker overlap phase in its convergence envelope.
 */
export const MAX_SUBSTEP_DISP = 0.125;
/** Hard cap on collision sub-moves per tick (7 needed at terminal velocity) */
export const MAX_SUBSTEPS = 12;
/**
 * Max ledge height auto-climbed while grounded, in meters (2.2 voxels: clears the 2-voxel
 * ledges players actually build and the 2-voxel stair riser, rejects 3 voxels with 0.2 m of
 * margin). Replaces the old implicit "push straight up on any steep contact" behaviour of
 * the mesh-BVH resolver, which ratcheted the player up roughly a foot-sphere diameter
 * (~0.5 m) — this is the bounded, explicit version of the traversal that felt effortless.
 *
 * This is the bound on the SOLVER's step query (`tryStepUp`), which is what actually mounts a
 * vertical riser — the vertical phase's own lift window can only absorb ground that rises
 * gently under the probes. Raising this alone does not make taller ledges climbable if the
 * step query is not reached.
 */
export const STEP_HEIGHT = 0.55;
/**
 * Downward margin (m) on the region the physics tick must KNOW before it will run.
 *
 * Named for a mechanism that no longer exists: this was the bound on a grounded downhill snap
 * probe, and that snap is gone (push-out cannot pull the capsule down, and reaching down to grab
 * the ground is what stopped the player ever leaving it). What survives is the number's other
 * job — sizing `REGION_DOWN_BASE` in `stepPlayer`, i.e. how far below the feet must be resident
 * before a tick is allowed to advance. Kept at its measured value rather than re-derived, because
 * the freeze contract is tuned around it.
 */
export const MAX_SNAP_DISTANCE = 0.25;
/**
 * How far below the target feet position ground support still counts, in meters. The
 * COLLISION_SKIN rest offset plus one 4-bit quantization step of surface jitter; without it
 * a standing capsule (feet at surface + SKIN) reads as airborne every tick. Also the floor
 * of the speed-proportional snap distance.
 */
export const SUPPORT_SLACK = 0.06;
/** Contact skin distance in meters — rest positions sit this far off the isosurface */
export const COLLISION_SKIN = 0.015;
/**
 * Minimum contact-normal Y to accept a step-up/uphill lift (0.5 → 60° from horizontal).
 * Steeper contacts are treated as walls. There is no slope limit for merely standing.
 */
export const MIN_STEP_NORMAL_Y = 0.5;
/** Gauss-Seidel iterations for the horizontal whisker overlap resolver */
export const MAX_WALL_ITERS = 4;
/** Gradient-push iterations in the depenetration ladder before escalating */
export const MAX_DEPEN_ITERS = 8;
/** Below this gradient magnitude the weight field counts as saturated (no direction info) */
export const GRAD_DEAD_EPS = 0.02;
/** Upward distance scanned for an air gap when depenetration needs an escape, in meters */
export const ESCAPE_SCAN_UP = 16;
/**
 * Weight substituted for solid-but-non-collidable corners (water/lava/leaves), byte-matching
 * the SurfaceNets mesher's FILTER_WEIGHT so the collision isosurface equals the rendered
 * solid layer's isosurface.
 */
export const COLLISION_FILTER_WEIGHT = -0.00001;
// ============== Player Dimensions ==============
/** Total height from feet to eyes in meters */
export const PLAYER_HEIGHT = 1.6;
/** Collision capsule radius in meters */
export const PLAYER_RADIUS = 0.25;
/** Height of capsule line segment (total height minus end caps) */
export const PLAYER_HEIGHT_INNER = PLAYER_HEIGHT - PLAYER_RADIUS * 2;
/** Ground level Y coordinate */
export const GROUND_LEVEL = 0;
// ============== Jump Assist ==============
/** Time after leaving ground where jump is still allowed (seconds) */
export const COYOTE_TIME = 0.15;
/**
 * Time before landing where a jump input is remembered and applied on land (seconds).
 * 0.15 s covers a full human reaction window, so chaining hops across surfaces does not
 * require frame-accurate timing on the landing tick.
 */
export const JUMP_BUFFER_TIME = 0.15;
// ============== Fall Detection ==============
/** Maximum continuous fall time before triggering respawn (seconds) */
export const MAX_FALL_TIME = 5.0;
// ============== Spawn Constants ==============
/** Height offset above terrain surface when spawning */
export const SPAWN_HEIGHT_OFFSET = 2.0;
/** Height to raycast from when finding terrain surface */
export const SPAWN_RAYCAST_HEIGHT = 200.0;
/** Fallback spawn height when terrain not found (high to let gravity work) */
export const SPAWN_FALLBACK_HEIGHT = 50.0;
//# sourceMappingURL=physics.js.map